import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Select, Option } from '@mui/joy';
import CustomPagination from '../components/CustomPagination';
import { billService } from '../api/bill';
import { customerService } from '../api/customer';
import { Customer } from '../api/customer';
import { Bill } from '../api/bill';
import { PAGE_TRANSLATIONS } from '../conf';

const BillPage: React.FC = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);

  const constructWhereData = (searchText: string, startDate: string, endDate: string, selectedCustomer: number | null) => {
    let where = "(_.officialCode ILIKE :searchText)"
    if (startDate) where += " AND _.officialDate >= :startDate";
    if (endDate) where += " AND _.officialDate <= :endDate";
    if (selectedCustomer) where += " AND _.customer.id = :customerId";
    const params = {
      searchText: `%${searchText}%`,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(selectedCustomer && { customerId: selectedCustomer })
    };
    return { where, params };
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customerResponse = await customerService.listAll();
        setCustomers(customerResponse);
      } catch (error) {
        console.error('Failed to fetch customers', error);
      }
    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    const loadBills = async () => {
      try {
        const whereData = constructWhereData(searchText, startDate, endDate, selectedCustomer);

        const options = {
          max: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          sort: '_.officialDate',
          order: 'DESC' as 'DESC',
          // ...(searchText && { q: searchText }),
          // ...(startDate && { startDate }),
          // ...(endDate && { endDate }),
          // ...(selectedCustomer && { customerId: selectedCustomer })
        };

        const response = await billService.where(whereData, options);
        setBills(response.data.data);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('Failed to load bills', error);
      }
    };
    loadBills();
  }, [currentPage, rowsPerPage, searchText, startDate, endDate, selectedCustomer]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setCurrentPage(1);
  };

  const handleCustomerChange = (newValue: string | null) => {
    setSelectedCustomer(newValue ? parseInt(newValue, 10) : null);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  return (
    <div>
      <h2>{PAGE_TRANSLATIONS.BILLS}</h2>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="searchText">{PAGE_TRANSLATIONS.SEARCH}</label>
          <Input
            id="searchText"
            ref={inputRef}
            placeholder={PAGE_TRANSLATIONS.BILL_CODE_PLACEHOLDER}
            value={searchText}
            onChange={handleFilterChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="startDate">{PAGE_TRANSLATIONS.START_DATE}</label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="endDate">{PAGE_TRANSLATIONS.END_DATE}</label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="customerSelect">{PAGE_TRANSLATIONS.CUSTOMERS}</label>
          <Select
            id="customerSelect"
            value={selectedCustomer !== null ? selectedCustomer.toString() : ''}
            onChange={(_e, newValue) => handleCustomerChange(newValue)}
            sx={{ minWidth: 200 }}
          >
            <Option value="">{PAGE_TRANSLATIONS.ALL_CUSTOMERS}</Option>
            {customers.map(customer => (
              <Option key={customer.id} value={customer.id.toString()}>{customer.name}</Option>
            ))}
          </Select>
        </div>
      </div>
      <Table>
        <thead>
          <tr>                                    
            <th>{PAGE_TRANSLATIONS.DATE}</th>
            <th>{PAGE_TRANSLATIONS.OFFICIAL_CODE}</th>
            <th>{PAGE_TRANSLATIONS.CUSTOMER_NAME}</th>
            <th>{PAGE_TRANSLATIONS.BALANCE}</th>
            <th>{PAGE_TRANSLATIONS.TAX_BALANCE}</th>
            <th>{PAGE_TRANSLATIONS.TOTAL}</th>            
          </tr>
        </thead>
        <tbody>
          {bills.length > 0 ? (
            bills.map((bill) => (
              <tr key={bill.id}>                                
              <td>{bill.officialDate ? new Date(bill.officialDate).toLocaleDateString() : '-'}</td>                                
                <td>{bill.officialCode || '-'}</td>
                <td>{bill.customer.name}</td>
                <td>{formatCurrency(bill.balance)}</td>
                <td>{formatCurrency(bill.taxBalance)}</td>
                <td>{formatCurrency(bill.balance + bill.taxBalance)}</td>                
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center' }}>{PAGE_TRANSLATIONS.NO_DATA}</td>
            </tr>
          )}
        </tbody>
      </Table>
      <CustomPagination
        count={totalCount}
        page={totalCount > 0 ? currentPage : 0}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default BillPage; 